'use client'

import { useCustomTokens } from '@sushiswap/hooks'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { Amount, getNativeAddress } from 'sushi'
import {
  type EvmAddress,
  type EvmChainId,
  type EvmCurrency,
  EvmNative,
  EvmToken,
} from 'sushi/evm'
import type { Address } from 'viem'
import { useBalances } from '~evm/_common/ui/balance-provider/use-balances'
import { fetchKatanaTokenList } from './katana-token-list'

interface UseMyTokens {
  chainId: EvmChainId | undefined
  account?: EvmAddress
  includeNative?: boolean
}

export function useMyTokens({ chainId, includeNative }: UseMyTokens) {
  const { data: customTokensData } = useCustomTokens()

  // Build the full list of currencies (Katana token list + custom tokens)
  const { data: tokenList } = useQuery({
    queryKey: ['katana-token-list-for-balances'],
    queryFn: fetchKatanaTokenList,
    staleTime: 15 * 60 * 1000,
  })

  const currencies = useMemo(() => {
    if (!chainId || !tokenList) return undefined

    const result: EvmCurrency<{ approved: boolean }>[] = []
    const seen = new Set<string>()
    const nativeAddress = getNativeAddress(chainId)

    for (const t of tokenList) {
      const addr = t.address.toLowerCase()
      if (seen.has(addr)) continue
      seen.add(addr)

      if (addr === nativeAddress.toLowerCase()) {
        if (includeNative) {
          result.push(EvmNative.fromChainId(chainId))
        }
      } else {
        result.push(
          new EvmToken({
            chainId,
            address: t.address as EvmAddress,
            name: t.name,
            symbol: t.symbol,
            decimals: t.decimals,
            metadata: { approved: true },
          }),
        )
      }
    }

    // Add custom tokens
    for (const t of Object.values(customTokensData)) {
      if (t.chainId !== chainId) continue
      const addr = t.address.toLowerCase()
      if (seen.has(addr)) continue
      seen.add(addr)

      result.push(
        new EvmToken({
          chainId,
          address: t.address as EvmAddress,
          name: t.name || '',
          symbol: t.symbol || '',
          decimals: t.decimals,
          metadata: { approved: false },
        }),
      )
    }

    return result
  }, [chainId, tokenList, customTokensData, includeNative])

  // Get addresses for the balance provider
  const tokenAddresses = useMemo(() => {
    if (!currencies || !chainId) return undefined
    return currencies.map((c) =>
      c.type === 'native'
        ? getNativeAddress(chainId)
        : (c.address as EvmAddress),
    )
  }, [currencies, chainId])

  const balancesResult = useBalances(chainId, tokenAddresses)

  return useMemo(() => {
    if (!currencies || !chainId || !balancesResult.data) {
      return {
        ...balancesResult,
        data: {
          tokens: [] as EvmCurrency<{ approved: boolean }>[],
          balanceMap: undefined,
        },
      }
    }

    const nativeAddress = getNativeAddress(chainId)
    const tokens: EvmCurrency<{ approved: boolean }>[] = []
    const balanceMap = new Map<Address, Amount<EvmCurrency>>()

    for (const currency of currencies) {
      const address =
        currency.type === 'native'
          ? nativeAddress
          : (currency.address as EvmAddress)
      const rawBalance = balancesResult.data.get(address)

      if (rawBalance === undefined || rawBalance === 0n) continue

      tokens.push(currency)
      balanceMap.set(address, new Amount(currency, rawBalance))
    }

    return {
      ...balancesResult,
      data: { tokens, balanceMap },
    }
  }, [currencies, chainId, balancesResult])
}
